
import React, { useState } from 'react';
import { Brain, Flame, Camera, Sparkles, CupSoda, Play, Trophy, ChevronRight, AudioLines, ChartColumnIncreasing, ChartColumnDecreasing, X, ChevronLeft } from 'lucide-react';
import { useLocale } from '../context/LocaleContext';

export const FeatureBanner = () => {
  const { t } = useLocale();
  const [currentIndex, setCurrentIndex] = useState(0);
  // Initialize visibility based on localStorage to persist dismissal across reloads
  const [isVisible, setIsVisible] = useState(() => {
    // Check if running in browser environment
    if (typeof window !== 'undefined') {
      return localStorage.getItem('forza_banner_dismissed') !== 'true';
    }
    return true;
  });

  const features = [
    {
      id: 0,
      icon: ChartColumnIncreasing,
      title: t('featureBanner.items.adaptiveTitle', 'Forza adapts with you'),
      desc: t('featureBanner.items.adaptiveDesc', 'Your quests evolve with your level, consistency, and goals.'),
      color: "from-cyan-700 to-cyan-500"
    },
    {
      id: 1,
      icon: ChartColumnDecreasing,
      title: t('featureBanner.items.missTitle', 'Do not let quests slip'),
      desc: t('featureBanner.items.missDesc', 'Missed quests reduce XP, so consistency keeps your momentum high.'),
      color: "from-red-800 to-red-600"
    },
    {
      id: 2,
      icon: Brain,
      title: t('featureBanner.items.coachTitle', 'AI Coach'),
      desc: t('featureBanner.items.coachDesc', 'Tap the AI Coach mic when you want advice or a smarter plan tweak.'),
      color: "from-indigo-600 to-purple-600"
    },
    {
      id: 3,
      icon: Camera,
      title: t('featureBanner.items.proofTitle', 'Proof of work'),
      desc: t('featureBanner.items.proofDesc', 'Snap 3 meal photos to verify the quest and lock in your XP.'),
      color: "from-orange-800 to-orange-400"
    },
    {
      id: 4,
      icon: Play,
      title: t('featureBanner.items.timedTitle', 'Timed workout sessions'),
      desc: t('featureBanner.items.timedDesc', 'Tap Verify, then Start Timer to launch a guided workout flow.'),
      color: "from-emerald-800 to-emerald-400"
    },
    {
      id: 5,
      icon: AudioLines,
      title: t('featureBanner.items.voiceTitle', 'Voice notes'),
      desc: t('featureBanner.items.voiceDesc', 'Send a voice note to verify your quest and collect the XP.'),
      color: "from-yellow-800 to-yellow-500"
    },
    {
      id: 6,
      icon: Trophy,
      title: t('featureBanner.items.levelTitle', 'Level up'),
      desc: (
        <span className="inline-flex items-center gap-1">
          {t('featureBanner.items.levelPrefix', 'Build streaks')}
          <Flame size={18} className="inline-block" />
          {t('featureBanner.items.levelSuffix', 'to unlock rewards.')}
        </span>
      ),
      color: "from-blue-800 to-blue-600"
    },
    {
      id: 7,
      icon: Sparkles,
      title: t('featureBanner.items.aurasTitle', 'Earn auras'),
      desc: t('featureBanner.items.aurasDesc', 'Quest completion earns 100 Auras. A 7-day streak earns 1000.'),
      color: "from-red-800 to-red-600"
    },
    {
      id: 8,
      icon: CupSoda,
      title: t('featureBanner.items.shopTitle', 'Spend auras wisely'),
      desc: (
        <span className="inline-flex items-center gap-1">
          {t('featureBanner.items.shopPrefix', 'Tap the')}
          <Sparkles size={18} className="inline-block" />
          {t('featureBanner.items.shopSuffix', 'to open the shop and activate Forza boosts.')}
        </span>
      ),
      color: "from-indigo-600 to-purple-600"
    },
  ];


  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % features.length);
  };

  const handleBefore = () => {
    setCurrentIndex((prev) => (prev - 1 + features.length) % features.length);
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    setIsVisible(false);
    // Persist the dismissal
    localStorage.setItem('forza_banner_dismissed', 'true');
  };

  if (!isVisible) return null;


  const currentFeature = features[currentIndex];

  return (
    <div className="mt-4 relative animate-fade-in-up">
      <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${currentFeature.color} p-3 shadow-lg border border-white/10 transition-colors duration-500`}>

        {/* Background Decoration */}
        <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full blur-2xl pointer-events-none"></div>

        <div className="flex items-start justify-between relative z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 backdrop-blur-md rounded-lg shadow-sm">
              <currentFeature.icon size={20} className="text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white text-sm">{currentFeature.title}</h3>
            </div>
          </div>
          <button
            onClick={handleDismiss}
            className="text-white/60 hover:text-white hover:bg-white/10 rounded-full p-1 transition-all"
            aria-label={t('featureBanner.dismiss', 'Dismiss guide')}
          >
            <X size={16} />
          </button>
        </div>
        <p className="text-xs text-white/90 mt-2 font-medium">{currentFeature.desc}</p>

        <div className="flex items-center justify-between mt-2">
          <div className="flex gap-1.5">
            {features.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`h-1.5 rounded-full transition-all duration-300 ${idx === currentIndex ? 'w-6 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/60'}`}
                aria-label={t('featureBanner.jumpTo', 'Go to feature {index}', { index: idx + 1 })}
              />
            ))}
          </div>
          <div className='flex items-center gap-2 justify-between'>
            <button
              onClick={handleBefore}
              className="flex items-center gap-1 text-xs font-semibold text-white hover:text-white/80 transition-colors"
            >
              <ChevronLeft size={12} />{t('featureBanner.previous', 'Previous')}
            </button>
            <button
              onClick={handleNext}
              className="flex items-center gap-1 text-xs font-semibold text-white hover:text-white/80 transition-colors"
            >
              {t('featureBanner.next', 'Next')} <ChevronRight size={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
